import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import BrandMark from "@/components/public/BrandMark";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

export default function RatePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [commentSent, setCommentSent] = useState(false);

  useEffect(() => {
    api.get(`/public/rate/${token}`).then((r) => {
      setInfo(r.data);
      if (r.data.rated) { setRating(r.data.rating || 0); setDone(true); }
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || "This link is invalid"));
  }, [token]);

  const submit = async (value, text = "") => {
    try {
      await api.post(`/public/rate/${token}`, { rating: value, comment: text });
      setRating(value);
      setDone(true);
      if (text) { setCommentSent(true); toast.success("Thanks for the feedback!"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save your rating"); }
  };

  return (
    <div className="public-site hero-glow flex min-h-screen flex-col items-center justify-center px-5 py-12">
      <div className="mb-8"><BrandMark dark testid="rate-logo" /></div>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/30 sm:p-10" data-testid="rate-card">
        {error && <Invalid error={error} />}
        {!error && !info && <p className="text-slate-400">Loading…</p>}
        {info && (
          <>
            {done ? <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" /> : <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">Order #{info.order_ref}</p>}
            <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900" data-testid="rate-title">
              {done ? "Thanks for rating!" : `How did ${info.laundry_name} do?`}
            </h1>
            <p className="mt-2 text-slate-500">{done ? `You rated ${info.laundry_name} ${rating} out of 5.` : `Hi ${info.customer_name || "there"}, tap a star to rate your order.`}</p>
            <div className="mt-6 flex justify-center gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" disabled={done} onClick={() => submit(n)} onMouseEnter={() => setHover(n)} aria-label={`${n} stars`} data-testid={`rate-star-${n}`}
                  className="rounded-full p-1 transition-transform hover:scale-110 disabled:hover:scale-100">
                  <Star className={`h-10 w-10 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <p className="mt-2 h-5 text-sm font-semibold text-slate-600">{LABELS[hover || rating]}</p>
            {done && !commentSent && (
              <div className="mt-6 text-left">
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything else to share? (optional)" data-testid="rate-comment" />
                <button onClick={() => submit(rating, comment)} disabled={!comment.trim()} data-testid="rate-comment-submit"
                  className="mt-3 h-11 w-full rounded-full bg-brand font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50">Send comment</button>
              </div>
            )}
            {info.demo && <p className="mt-6 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="rate-demo-note">This is a test link, so nothing is saved.</p>}
          </>
        )}
      </motion.div>
      <Link to="/" className="mt-6 text-sm font-semibold text-white/60 hover:text-white" data-testid="rate-home-link">Back to the homepage</Link>
    </div>
  );
}

function Invalid({ error }) {
  return (
    <div data-testid="rate-invalid">
      <AlertCircle className="mx-auto h-12 w-12 text-slate-300" />
      <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Link not available</h1>
      <p className="mt-2 text-slate-500">{error}</p>
    </div>
  );
}
